/**
 * EventPage: the forensic record for a single alert at /event/{id}.
 *
 * Design doc section 5 layout:
 *   Header: back-to-timeline link (disabled) | event UUID (mono) | Share button
 *   Main (left ~70%): FrozenMapSnapshot -> RssiWaveform -> CameraClip (or placeholder)
 *   Sidebar (right ~30%): MetadataSidebar -> RelatedEventsPanel
 *
 * Data sources:
 *   GET /v1/alerts/{id}          -> AlertDetail (via useAlertDetail)
 *   GET /v1/alerts/{id}/related  -> AlertRelated[] (via useAlertRelated, parallel)
 *
 * Cross-org access: fleet-api returns 404 for alerts belonging to another org.
 * The UI renders a neutral empty state ("not found / may belong to another org")
 * to avoid leaking existence of foreign resources.
 *
 * Phase deferrals:
 *   - "Back to timeline" link is disabled; /timeline ships in Phase 4.
 *   - PDF export of single event: Phase 5+.
 *   - Acknowledgement UI: Phase 4 staff-side.
 */
import { lazy, Suspense, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import * as styles from './EventPage.css';
import {
  useAlertDetail,
  useAlertRelated,
  useEntryEventDetail,
} from './queries';
import { ApiError } from '@/lib/api-client';
import { FrozenMapSnapshot } from './FrozenMapSnapshot';
import { RssiWaveform } from './RssiWaveform';
import { CameraClip } from './CameraClip';
import { MetadataSidebar } from './MetadataSidebar';
import { RelatedEventsPanel } from './RelatedEventsPanel';
import { ShareButton } from './ShareButton';
import { theme } from '@/design/tokens.css';

/**
 * Phase 12 Plan 12-11: lazy-load the EntryEventDrill so its SENTINEL
 * string + render body stay out of the pre-auth entry chunk. Loaded
 * only when the URL parses to kind='entry'.
 */
const EntryEventDrill = lazy(() =>
  import('./EntryEventDrill').then((m) => ({ default: m.EntryEventDrill })),
);

// ---------------------------------------------------------------------------
// URL discriminator (Phase 12 Plan 12-11)
// ---------------------------------------------------------------------------

/**
 * Parsed shape from the /event/:id URL param.
 *
 * `kind` is one of:
 *   - 'alert': the URL form is /event/<numeric> OR /event/alert-<numeric>
 *   - 'entry': the URL form is /event/entry-<numeric>
 *
 * `isBareLegacy` distinguishes /event/123 (legacy: alert-first with
 * entry-event 404 fallback) from /event/alert-123 (explicit: alert
 * only, no fallback). The fallback path is preserved so bookmarks
 * from before Plan 12-11 keep working.
 */
interface ParsedEventId {
  kind: 'alert' | 'entry';
  numeric: number;
  isBareLegacy: boolean;
}

/**
 * Parse the raw /event/:id param into kind + numeric id.
 *
 * Accepted forms:
 *   /event/123        -> { kind: 'alert', numeric: 123, isBareLegacy: true }
 *   /event/alert-123  -> { kind: 'alert', numeric: 123, isBareLegacy: false }
 *   /event/entry-456  -> { kind: 'entry', numeric: 456, isBareLegacy: false }
 *
 * Returns null for anything else (e.g. /event/abc, /event/, empty).
 * The caller renders a 404 empty state in that case.
 *
 * @param raw - The URL :id segment, or undefined
 * @returns Parsed shape, or null when no parse succeeds
 */
export function parseEventId(raw: string | undefined): ParsedEventId | null {
  if (!raw) return null;
  const prefixMatch = raw.match(/^(alert|entry)-(\d+)$/);
  if (prefixMatch) {
    const kind = prefixMatch[1] as 'alert' | 'entry';
    return { kind, numeric: Number(prefixMatch[2]), isBareLegacy: false };
  }
  const bareMatch = raw.match(/^(\d+)$/);
  if (bareMatch) {
    return { kind: 'alert', numeric: Number(bareMatch[1]), isBareLegacy: true };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

/**
 * Skeleton screen shown while alert detail loads.
 * Provides layout continuity without a disruptive spinner.
 */
function EventPageSkeleton() {
  return (
    <div className={styles.skeletonPage} data-testid="event-page-skeleton">
      <div className={styles.skeletonBase} style={{ height: '48px', width: '100%' }} />
      <div className={styles.skeletonBase} style={{ height: '320px', width: '100%' }} />
      <div className={styles.skeletonBase} style={{ height: '160px', width: '100%' }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

/** Props for the empty state component. */
interface EmptyStateProps {
  /** Primary message (shown as a heading). */
  message: string;
  /** Path to navigate back to when the user clicks "Back to NOW". */
  backTo: string;
}

/**
 * Neutral empty state rendered on 404 or unexpected error.
 *
 * Uses neutral language for 404 to avoid leaking cross-org resource existence
 * (Phase 1.5 pen-test invariant: 404 not 403 on cross-org access).
 */
function EmptyState({ message, backTo }: EmptyStateProps) {
  return (
    <div className={styles.emptyState} data-testid="event-empty-state">
      <p className={styles.emptyStateTitle}>{message}</p>
      <Link to={backTo} className={`${styles.backLink} ${styles.backLinkActive}`}>
        Back to NOW
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Camera placeholder
// ---------------------------------------------------------------------------

/**
 * Placeholder shown when the alert has no camera clip URL.
 *
 * Phase 3: clip URL may be absent if the camera service did not capture a
 * clip, or if the presigned URL expired. A future phase adds server-side
 * URL refresh for stale presigned URLs.
 */
function CameraPlaceholder() {
  return (
    <div
      style={{
        padding: '24px',
        // Routed through theme token so the placeholder text legibility
        // tracks the active SOC palette in both modes.
        color: theme.colors.text.tertiary,
        fontSize: '13px',
        fontFamily: theme.fonts.mono,
        textAlign: 'center',
      }}
      data-testid="camera-placeholder"
    >
      No camera evidence for this event.
    </div>
  );
}

// ---------------------------------------------------------------------------
// EventPage
// ---------------------------------------------------------------------------

/**
 * Resolve a contextual back-link target based on how the user reached this
 * event page. Three sources are consulted in priority order:
 *
 *   1. router state (location.state.from) - set by Link `state` props from
 *      the originating page (e.g. `<Link to="/event/123" state={{ from: '/now' }} />`).
 *      This is the primary signal because it is in-app and survives reloads
 *      via the History API state.
 *   2. document.referrer - HTTP referrer fallback for cases where the link
 *      did not pass router state (e.g. external link, manual paste, refresh).
 *   3. null - no contextual back link is rendered.
 *
 * Returns either a { to, label } pair or null. The router-state branch is
 * checked first because it is the most precise; the referrer branch matches
 * the path substring (so /admin/sites/abc and /admin/alerts both classify
 * as admin, and /now stays distinct).
 *
 * @param state - Router location state (from useLocation().state)
 * @returns Back-link descriptor or null
 */
function resolveBackLink(state: unknown): { to: string; label: string } | null {
  // Priority 1: explicit router state from the originating Link.
  if (state && typeof state === 'object' && 'from' in state) {
    const from = (state as { from?: unknown }).from;
    if (typeof from === 'string') {
      if (from.startsWith('/admin')) {
        return { to: '/admin/fleet', label: 'Back to admin fleet' };
      }
      if (from.startsWith('/now')) {
        return { to: '/now', label: 'Back to live view' };
      }
    }
  }
  // Priority 2: document.referrer (HTTP referrer header).
  if (typeof document !== 'undefined' && document.referrer) {
    try {
      const refUrl = new URL(document.referrer);
      if (refUrl.origin === window.location.origin) {
        if (refUrl.pathname.startsWith('/admin')) {
          return { to: '/admin/fleet', label: 'Back to admin fleet' };
        }
        if (refUrl.pathname.startsWith('/now')) {
          return { to: '/now', label: 'Back to live view' };
        }
      }
    } catch {
      // Malformed referrer URL - ignore and fall through.
    }
  }
  return null;
}

/**
 * Full forensics page for a single alert.
 *
 * Parses the :id URL param as a number. Non-numeric IDs render the not-found
 * empty state immediately (no fetch). Numeric IDs trigger useAlertDetail +
 * useAlertRelated in parallel.
 *
 * Phase 4 amendment 2026-04-27: a contextual back-link in the header points
 * the user back to whichever surface they came from. Admin referrers route
 * back to /admin/fleet ("Back to admin fleet"); /now referrers route back
 * to /now ("Back to live view"). The "Back to NOW" link below it remains
 * unconditional as a safe fallback.
 */
export function EventPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const parsed = parseEventId(id);
  const contextualBack = resolveBackLink(location.state);

  // Shared time cursor state: CameraClip sets it, RssiWaveform reads it.
  // null means no cursor active (waveform shows no time marker).
  const [timeCursor, setTimeCursor] = useState<number | null>(null);

  // ---------------------------------------------------------------------
  // URL discriminator branches (Phase 12 Plan 12-11)
  // ---------------------------------------------------------------------
  //
  // Hooks always run unconditionally (rules of hooks). We gate which one
  // actually fires via the `enabled` flag inside each hook so the parser
  // result decides what's fetched without forcing conditional invocation.
  //
  // Routing:
  //   parsed.kind === 'alert'  -> useAlertDetail fires for parsed.numeric.
  //   parsed.kind === 'entry'  -> useEntryEventDetail fires for parsed.numeric.
  //   parsed === null          -> both disabled; 404 empty state below.
  //
  // Legacy fallback:
  //   parsed.isBareLegacy + alert 404 -> retry as entry-event id.
  //   parsed.isBareLegacy + entry 404 -> standard 404 empty state.
  //   parsed.kind explicit -> NO fallback (prevents an unnecessary
  //                         round-trip on known-kind navigations).

  const alertIdForFetch =
    parsed && parsed.kind === 'alert' ? parsed.numeric : 0;

  // First-pass: try alert detail for both 'alert' and bare-legacy URLs.
  const {
    data: alert,
    isLoading: alertLoading,
    isError: alertIsError,
    error: alertError,
  } = useAlertDetail(alertIdForFetch);

  const { data: related = [] } = useAlertRelated(alertIdForFetch);

  // Did the alert fetch yield a 404? Used to decide whether to fall
  // back to the entry-event lookup on bare-legacy URLs.
  const alertWas404 =
    alertIsError && alertError instanceof ApiError && alertError.status === 404;
  // ApiError on the fetcher path is swallowed and returns null; treat
  // that as a 404 for the fallback decision so bare-legacy URLs cross
  // over even on the silent-null path.
  const alertReturnedNull =
    !alertLoading && !alertIsError && alert === null;

  // Decide the entry-event id to fetch:
  //   - parsed.kind === 'entry' -> use it directly.
  //   - parsed.kind === 'alert' AND isBareLegacy AND (404 || null) -> fall back.
  //   - everything else -> 0 (disables the hook).
  let entryEventIdForFetch = 0;
  if (parsed) {
    if (parsed.kind === 'entry') {
      entryEventIdForFetch = parsed.numeric;
    } else if (parsed.isBareLegacy && (alertWas404 || alertReturnedNull)) {
      entryEventIdForFetch = parsed.numeric;
    }
  }
  const entryEventQuery = useEntryEventDetail(
    entryEventIdForFetch > 0 ? entryEventIdForFetch : null,
  );

  // Unparseable :id -> immediate empty state, no fetch.
  if (parsed === null) {
    return (
      <EmptyState
        message="Event not found. It may belong to another organization."
        backTo="/now"
      />
    );
  }

  // ---------------------------------------------------------------------
  // 'entry' branch: render the EntryEventDrill (Phase 12 Plan 12-11).
  //
  // Two activation paths:
  //   (a) parsed.kind === 'entry'           - explicit URL
  //   (b) parsed.isBareLegacy + alert null  - bare-legacy fallback
  //
  // Same loading + 404 surface as the alert branch so the UX stays
  // uniform. For bare-legacy fallback we render the standard 404
  // empty state on ANY entry-event failure (404, network error, etc.)
  // because the fallback was a guess and the user's URL was an
  // ambiguous bare id - "not found" is the truthful answer.
  // For an explicit /event/entry-<id> URL we keep the generic error
  // message on non-404 failures so an actual network error surfaces.
  // ---------------------------------------------------------------------
  if (parsed.kind === 'entry' || (parsed.isBareLegacy && (alertWas404 || alertReturnedNull))) {
    if (entryEventQuery.isLoading) return <EventPageSkeleton />;
    if (entryEventQuery.isError) {
      // Bare-legacy fallback: collapse all errors to the 404 empty state.
      // Explicit entry URL: preserve the 404 vs generic-error split.
      if (parsed.isBareLegacy) {
        return (
          <EmptyState
            message="Event not found. It may belong to another organization."
            backTo="/now"
          />
        );
      }
      const err = entryEventQuery.error;
      if (err instanceof ApiError && err.status === 404) {
        return (
          <EmptyState
            message="Event not found. It may belong to another organization."
            backTo="/now"
          />
        );
      }
      return (
        <EmptyState
          message="Something went wrong loading this event."
          backTo="/now"
        />
      );
    }
    if (!entryEventQuery.data) {
      // null from fetchEntryEventDetail's 404-swallow path.
      return (
        <EmptyState
          message="Event not found. It may belong to another organization."
          backTo="/now"
        />
      );
    }
    return (
      <Suspense fallback={<EventPageSkeleton />}>
        <EntryEventDrill event={entryEventQuery.data} />
      </Suspense>
    );
  }

  // ---------------------------------------------------------------------
  // 'alert' branch: original Phase 3 alert-detail rendering preserved.
  // ---------------------------------------------------------------------

  if (alertLoading) return <EventPageSkeleton />;

  if (alertIsError) {
    if (alertError instanceof ApiError && alertError.status === 404) {
      return (
        <EmptyState
          message="Event not found. It may belong to another organization."
          backTo="/now"
        />
      );
    }
    return <EmptyState message="Something went wrong loading this event." backTo="/now" />;
  }

  // null data (returned by fetchAlertDetail on 404) is treated as not-found.
  if (!alert) {
    return (
      <EmptyState
        message="Event not found. It may belong to another organization."
        backTo="/now"
      />
    );
  }

  const hasClip = alert.has_camera_clip && !!alert.camera_clip_url;

  return (
    <div className={styles.page} data-testid="event-page">
      {/* Sticky header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          {/*
            Contextual back-link wayfinding (Phase 4 amendment 2026-04-27).
            Renders only when resolveBackLink returned a target. Admin
            referrers route to /admin/fleet; /now referrers route back to
            /now. When neither matched, the unconditional "Back to NOW"
            link below remains the only back affordance.
          */}
          {contextualBack && (
            <Link
              to={contextualBack.to}
              className={`${styles.backLink} ${styles.backLinkActive}`}
              data-testid="back-contextual"
            >
              <ArrowLeft size={14} />
              {contextualBack.label}
            </Link>
          )}

          {/* Back to timeline - disabled (Phase 4) */}
          <span
            className={styles.backLink}
            aria-disabled="true"
            title="Timeline view (Phase 4)"
            role="link"
            data-testid="back-to-timeline"
          >
            <ArrowLeft size={14} />
            Back to timeline
          </span>

          {/* Back to NOW - always active */}
          <Link
            to="/now"
            className={`${styles.backLink} ${styles.backLinkActive}`}
            data-testid="back-to-now"
          >
            Back to NOW
          </Link>
        </div>

        <div className={styles.headerRight}>
          <span className={styles.eventId} data-testid="event-id-display">
            #{alert.id}
          </span>
          {/*
            Phase 10 Plan 10-02: deep-link to the visual replay page for
            this alert id. Reuses the muted backLinkActive style so the
            link doesn't visually overpower the share button.
          */}
          <Link
            to={`/alerts/${alert.id}/replay`}
            className={`${styles.backLink} ${styles.backLinkActive}`}
            data-testid="event-view-replay-link"
            state={{ from: '/event' }}
          >
            View replay
          </Link>
          <ShareButton />
        </div>
      </header>

      {/* Two-column grid */}
      <div className={styles.grid}>
        {/* Main content: map, waveform, camera */}
        <main className={styles.main} data-testid="event-main">
          <FrozenMapSnapshot
            siteId={alert.site_id}
            zoneId={alert.zone_id}
            alertTs={alert.ts}
          />

          <RssiWaveform alert={alert} timeCursor={timeCursor} />

          {hasClip ? (
            <CameraClip
              url={alert.camera_clip_url!}
              thumbnailUrl={alert.thumbnail_url}
              onTimeUpdate={setTimeCursor}
            />
          ) : (
            <CameraPlaceholder />
          )}
        </main>

        {/* Sidebar: metadata + related events */}
        <aside className={styles.sidebar} data-testid="event-sidebar">
          <MetadataSidebar alert={alert} />
          <RelatedEventsPanel events={related} />
        </aside>
      </div>
    </div>
  );
}
